// @flow

/**
 * Answer Detail Component
 * Renders the <answer-detail> block and owns the detail toggle button. The
 * summary above is always visible; this component only shows/hides the detail.
 * Collapsed by default: only the "Show detail" button renders in place of the
 * detail. Expanded: the detail renders after the summary, followed by a
 * "Hide detail" button. During export the collapsed detail is mounted inside a
 * clipped wrapper so its charts can be captured.
 */

import React from 'react';
import styled from 'styled-components';
import { Button } from '@blueprintjs/core';
import { useAnswerToggle } from './AnswerToggleContext';
import ClippedForExport from './ClippedForExport';

const ToggleRow = styled.div`
  margin: 0.5rem 0;
`;

type Props = {
  children?: React$Node,
};

const AnswerDetail = ({ children }: Props): React$Node => {
  const toggle = useAnswerToggle();

  // Context is null/absent: tag used outside a chat message — render as-is.
  if (!toggle) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  const { showDetail, setShowDetail, isMessageStreaming, exportMode } = toggle;

  const toggleButton = (
    <ToggleRow>
      <Button
        minimal
        small
        icon={showDetail ? 'chevron-up' : 'chevron-down'}
        disabled={isMessageStreaming}
        aria-expanded={showDetail}
        onClick={() => setShowDetail(!showDetail)}
      >
        {showDetail ? 'Hide detail' : 'Show detail'}
      </Button>
    </ToggleRow>
  );

  if (showDetail) {
    return (
      <React.Fragment>
        {children}
        {toggleButton}
      </React.Fragment>
    );
  }

  // showDetail === false: button only (plus clipped detail during export).
  return (
    <React.Fragment>
      {toggleButton}
      {exportMode ? <ClippedForExport>{children}</ClippedForExport> : null}
    </React.Fragment>
  );
};

export default AnswerDetail;
